// ============================================
// UTILITY FUNCTIONS
// ============================================

// ============================================
// ERROR HANDLING
// ============================================

/**
 * User-friendly error messages
 */
const ERROR_MESSAGES = {
    // Auth errors
    'Invalid login credentials': 'Incorrect email or password. Please try again.',
    'Email not confirmed': 'Please check your email to confirm your account.',
    'User already registered': 'An account with this email already exists. Try signing in.',
    'Password should be at least 6 characters': 'Password must be at least 6 characters.',

    // Payment errors
    'card_declined': 'Your card was declined. Please try a different card.',
    'expired_card': 'Your card has expired. Please use a different card.',
    'insufficient_funds': 'Insufficient funds. Please try a different card.',
    'incorrect_cvc': 'Incorrect CVC code. Please check and try again.',

    // Network errors
    'Failed to fetch': 'Network error. Please check your internet connection.',
    'NetworkError': 'Network error. Please check your internet connection.',
    'TypeError: Failed to fetch': 'Network error. Please check your internet connection.',

    // Generic
    'default': 'Something went wrong. Please try again.',
};

/**
 * Get user-friendly error message
 */
export function getUserFriendlyError(error) {
    if (!error) return ERROR_MESSAGES.default;

    const message = error.message || error.toString();

    // Check for exact matches
    if (ERROR_MESSAGES[message]) {
        return ERROR_MESSAGES[message];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
        if (message.toLowerCase().includes(key.toLowerCase())) {
            return value;
        }
    }

    // Return original message if it's short and user-friendly
    if (message.length < 100 && !message.includes('Error:')) {
        return message;
    }

    return ERROR_MESSAGES.default;
}

/**
 * Global error handler for unhandled promise rejections
 */
export function setupGlobalErrorHandler() {
    window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled promise rejection:', event.reason);

        // Don't show toast for minor errors
        if (event.reason?.message?.includes('ResizeObserver')) {
            return;
        }

        // Show toast for user-facing errors
        if (typeof window.showToast === 'function') {
            window.showToast(getUserFriendlyError(event.reason), 'error');
        }
    });
}

// ============================================
// LOADING STATES
// ============================================

/**
 * Show loading state on a button
 */
export function setButtonLoading(button, isLoading, loadingText = 'Loading...') {
    if (!button) return;

    const originalText = button.dataset.originalText || button.textContent;

    if (isLoading) {
        button.dataset.originalText = originalText;
        button.disabled = true;
        button.innerHTML = `<span class="spinner-inline"></span> ${loadingText}`;
    } else {
        button.disabled = false;
        button.textContent = button.dataset.originalText || originalText;
        delete button.dataset.originalText;
    }
}

/**
 * Show loading overlay on a container
 */
export function showLoadingOverlay(containerId, show = true) {
    const container = document.getElementById(containerId);
    if (!container) return;

    let overlay = container.querySelector('.loading-overlay');

    if (show) {
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.className = 'loading-overlay';
            overlay.innerHTML = '<div class="loading-spinner"></div>';
            container.style.position = 'relative';
            container.appendChild(overlay);
        }
        overlay.style.display = 'flex';
    } else if (overlay) {
        overlay.style.display = 'none';
    }
}

// ============================================
// NETWORK STATUS
// ============================================

let isOnline = navigator.onLine;
let offlineToastShown = false;

/**
 * Setup network status monitoring
 */
export function setupNetworkMonitoring() {
    window.addEventListener('online', () => {
        isOnline = true;
        if (offlineToastShown && typeof window.showToast === 'function') {
            window.showToast('Back online', 'success');
            offlineToastShown = false;
        }
    });

    window.addEventListener('offline', () => {
        isOnline = false;
        if (typeof window.showToast === 'function') {
            window.showToast('You are offline. Some features may be unavailable.', 'error');
            offlineToastShown = true;
        }
    });
}

/**
 * Check if user is online
 */
export function checkOnline() {
    return isOnline;
}

// ============================================
// DEBOUNCE / THROTTLE
// ============================================

/**
 * Debounce function calls
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Throttle function calls
 */
export function throttle(func, limit) {
    let inThrottle;
    return function executedFunction(...args) {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

// ============================================
// VALIDATION
// ============================================

/**
 * Validate email format
 */
export function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Sanitize user input (prevent XSS)
 */
export function sanitizeInput(input) {
    if (typeof input !== 'string') return input;

    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
}
